/* Purely decorative; aria-hidden in the markup. Every animation stops under
   prefers-reduced-motion, including the pointer tracking (skipped in JS). */

.base {
  position: fixed;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;

  --pointer-x: 50vw;
  --pointer-y: 50vh;
}

.gradient {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 80% 50% at 50% -20%, var(--accent-primary-muted) 0%, transparent 60%),
    radial-gradient(ellipse 60% 40% at 80% 100%, var(--accent-secondary-muted) 0%, transparent 50%),
    var(--bg-base);
}

/* ── Drifting grid ── */

.grid {
  position: absolute;
  inset: -60px;
  background-image:
    linear-gradient(var(--border-subtle) 1px, transparent 1px),
    linear-gradient(90deg, var(--border-subtle) 1px, transparent 1px);
  background-size: 60px 60px;
  opacity: 0.5;
  animation: gridDrift 30s linear infinite;
}

@keyframes gridDrift {
  to {
    transform: translate(60px, 60px);
  }
}

/* ── Floating data nodes ── */

.nodes {
  position: absolute;
  inset: 0;
}

/* Defaults so the rule resolves on its own; the per-node inline style overrides them. */
.node {
  --node-x: 50%;
  --node-y: 50%;
  --node-delay: 0s;
  --node-duration: 8s;

  position: absolute;
  left: var(--node-x);
  top: var(--node-y);
  width: 4px;
  height: 4px;
  border-radius: var(--radius-round);
  opacity: 0;
  animation: nodeFloat var(--node-duration) ease-in-out infinite;
  animation-delay: var(--node-delay);
}

.tonePrimary {
  background: var(--accent-primary);
}

.toneSecondary {
  background: var(--accent-secondary);
}

.toneSuccess {
  background: var(--color-success);
}

@keyframes nodeFloat {
  0%,
  100% {
    transform: translateY(0) scale(1);
    opacity: 0.2;
  }
  50% {
    transform: translateY(-20px) scale(1.6);
    opacity: 0.5;
  }
}

/* ── Pulse rings ── */

.pulse {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 400px;
  height: 400px;
  margin: -200px 0 0 -200px;
  border: 1px solid var(--accent-primary);
  border-radius: var(--radius-round);
  opacity: 0;
  animation: pulseRing 4s ease-out infinite;
}

.pulse:nth-of-type(2) {
  border-color: var(--accent-secondary);
  animation-delay: 2s;
}

@keyframes pulseRing {
  0% {
    transform: scale(0.3);
    opacity: 0.15;
  }
  100% {
    transform: scale(2);
    opacity: 0;
  }
}

/* ── Corner tints ── */

.tintTop,
.tintBottom {
  position: absolute;
  width: 700px;
  height: 700px;
  border-radius: var(--radius-round);
  filter: blur(80px);
  opacity: 0.1;
}

.tintTop {
  top: -300px;
  left: -300px;
  background: var(--accent-secondary);
}

.tintBottom {
  bottom: -300px;
  right: -300px;
  background: var(--accent-secondary);
}

[data-theme='light'] .tintTop,
[data-theme='light'] .tintBottom {
  opacity: 0.15;
}

/* ── Pointer-following orb ── */

.orb {
  position: absolute;
  left: 0;
  top: 0;
  width: 300px;
  height: 300px;
  border-radius: var(--radius-round);
  background: radial-gradient(circle, var(--accent-secondary-muted) 0%, transparent 70%);

  /* Positioned by transform rather than left/top so the trail runs on the
     compositor instead of forcing a layout pass per frame. The -50% centres it. */
  transform: translate(calc(var(--pointer-x) - 50%), calc(var(--pointer-y) - 50%));
  /* ease-out, not --ease-default: that token is ease-in-out, and its slow start
     restarts on every pointer move, which reads as lag rather than trail. */
  transition: transform var(--duration-slow) ease-out;
  will-change: transform;
}

@media (prefers-reduced-motion: reduce) {
  .grid,
  .node,
  .pulse {
    animation: none;
  }

  .node {
    opacity: 0.3;
  }

  .pulse {
    display: none;
  }

  /* Stays visible, just still — reduce-motion means stop movement, not strip
     decoration. With no listener attached it rests centred. */
  .orb {
    transition: none;
  }
}
